import { useEffect, useRef } from "react";
import { INTRO, bayer } from "./intro";

// CSS pixels per art pixel: finer on phones so the scene keeps its detail
const pixelSize = (width) => (width < 600 ? 2 : 3);
const FPS = 20;

const BG = [10, 10, 10];
const SKY = [
  [10, 10, 12],
  [13, 14, 24],
  [17, 19, 38],
  [24, 25, 54],
  [36, 33, 70],
  [58, 44, 86],
  [92, 58, 96],
  [134, 76, 96],
  [176, 98, 92],
];
const MOON = [244, 236, 214];
const MOON_SHADE = [214, 204, 182];
const MOON_CRATER = [196, 186, 164];
const FAR = [40, 36, 72];
const FAR_RIM = [66, 58, 104];
const MID = [24, 23, 46];
const MID_DEEP = [19, 18, 36];
const HILL = [12, 12, 20];
const GROUND = [11, 11, 15];
const WATER = [6, 8, 18];
const BIRDS = [18, 16, 30];
const STAR = [255, 250, 232];
const FIREFLY = [255, 204, 112];
const LANTERN = [255, 214, 140];
const RIPPLE = [214, 204, 236];
const LANTERN_RADIUS = 14; // art pixels
const RIPPLE_LIFE = 1.4; // seconds

// Classic pixel arrow: # is the dark outline, o the moonlit fill
const CURSOR = [
  "#.........",
  "##........",
  "#o#.......",
  "#oo#......",
  "#ooo#.....",
  "#oooo#....",
  "#ooooo#...",
  "#oooooo#..",
  "#ooooooo#.",
  "#oooo#####",
  "#oo#o#....",
  "#o#.#o#...",
  "##..#o#...",
  "#....#o#..",
  ".....#o#..",
  "......##..",
];

// Draw the arrow at 2x with hard edges and hand it to CSS as a PNG cursor
const pixelCursor = () => {
  const scale = 2;
  const c = document.createElement("canvas");
  c.width = CURSOR[0].length * scale;
  c.height = CURSOR.length * scale;
  const ctx = c.getContext("2d");
  CURSOR.forEach((row, y) =>
    [...row].forEach((cell, x) => {
      if (cell === ".") return;
      ctx.fillStyle = cell === "#" ? "#0a0a0a" : "#f4ecd6";
      ctx.fillRect(x * scale, y * scale, scale, scale);
    })
  );
  return `url(${c.toDataURL()}) 0 0, crosshair`;
};

const seeded = (seed) => () => {
  seed = (seed * 1664525 + 1013904223) >>> 0;
  return seed / 4294967296;
};

const valueNoise = (seed) => {
  const rand = seeded(seed);
  const pts = Array.from({ length: 64 }, rand);
  return (x) => {
    const i = Math.floor(x);
    const f = x - i;
    const a = pts[((i % 64) + 64) % 64];
    const b = pts[(((i + 1) % 64) + 64) % 64];
    return a + (b - a) * f * f * (3 - 2 * f);
  };
};

const ridge = (noise, x) =>
  noise(x) * 0.6 + noise(x * 2.3 + 11) * 0.28 + noise(x * 5.1 + 37) * 0.12;

// The favicon hen as a hard edged silhouette mask
const loadHen = (height) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const w = Math.round(height * 0.95);
      const c = document.createElement("canvas");
      c.width = w;
      c.height = height;
      const ctx = c.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, w, height);
      const { data } = ctx.getImageData(0, 0, w, height);
      const mask = new Uint8Array(w * height);
      for (let i = 0; i < mask.length; i++) mask[i] = data[i * 4 + 3] > 120 ? 1 : 0;
      resolve({ w, h: height, mask, rgba: data });
    };
    img.onerror = () => resolve(null);
    img.src = `${process.env.PUBLIC_URL}/hen.png`;
  });

function buildScene(W, H) {
  const rand = seeded(8);
  const horizon = Math.round(H * 0.6);
  const lakeEnd = Math.round(H * 0.85);
  const moon = { x: Math.round(W * 0.6), y: Math.round(H * 0.38), r: Math.max(9, Math.round(W * 0.12)) };
  const crest = Math.round(moon.y + moon.r * 0.62);

  const far = valueNoise(3);
  const mid = valueNoise(17);
  const shore = valueNoise(29);

  const farY = new Int16Array(W);
  const midY = new Int16Array(W);
  const hillY = new Int16Array(W);
  const groundY = new Int16Array(W);
  for (let x = 0; x < W; x++) {
    const u = x / W;
    // A valley opens up where the moon rises
    const valley = 1 - 0.75 * Math.exp(-(((x - moon.x) / (W * 0.2)) ** 2));
    farY[x] = Math.round(horizon - H * (0.07 + 0.2 * ridge(far, u * 3.2)) * valley);
    midY[x] = Math.round(horizon - H * (0.02 + 0.09 * ridge(mid, u * 4.5 + 5)));
    hillY[x] = Math.round(
      horizon - (horizon - crest) * Math.exp(-(Math.abs((x - moon.x) / (W * 0.16)) ** 2.2))
    );
    groundY[x] = Math.round(lakeEnd - H * 0.015 + H * 0.03 * ridge(shore, u * 6));
  }

  const craters = Array.from({ length: 5 }, () => ({
    x: moon.x + (rand() - 0.5) * moon.r * 1.2,
    y: moon.y + (rand() - 0.5) * moon.r * 1.2,
    r: moon.r * (0.1 + rand() * 0.16),
  }));

  const base = new Uint8ClampedArray(W * H * 4);
  const skyMask = new Uint8Array(W * H); // open sky away from the moon's glow
  const openSky = new Uint8Array(W * H); // any sky pixel that isn't the moon
  const put = (x, y, c) => {
    const i = (y * W + x) * 4;
    base[i] = c[0];
    base[i + 1] = c[1];
    base[i + 2] = c[2];
    base[i + 3] = 255;
  };

  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const d = bayer(x, y);
      if (y >= lakeEnd - H * 0.02 && y >= groundY[x]) {
        put(x, y, GROUND);
        continue;
      }
      if (y >= horizon) continue; // lake, drawn per frame

      if (y >= hillY[x]) {
        put(x, y, HILL);
      } else if (y >= midY[x]) {
        const depth = (y - midY[x]) / (horizon - midY[x] + 1);
        put(x, y, depth > d * 0.9 + 0.1 ? MID_DEEP : MID);
      } else if (y >= farY[x]) {
        // Moonlit rim along the far ridge
        const rim = y - farY[x] < 2 && Math.abs(x - moon.x) < W * 0.45;
        put(x, y, rim && d > 0.35 ? FAR_RIM : FAR);
      } else {
        const md = Math.hypot(x - moon.x, y - moon.y);
        if (md <= moon.r) {
          let c = MOON;
          // Shade the lower left limb with dither
          const shade = ((moon.x - x) * 0.6 + (y - moon.y) * 0.8) / moon.r;
          if (shade > 0.35 + d * 0.5) c = MOON_SHADE;
          for (const k of craters) {
            if (Math.hypot(x - k.x, y - k.y) < k.r) c = MOON_CRATER;
          }
          put(x, y, c);
        } else {
          let v = (y / horizon) ** 1.6 * (SKY.length - 1);
          const glow = Math.max(0, 1 - (md - moon.r) / (moon.r * 2.6));
          v = Math.min(SKY.length - 1, v + glow * glow * 2.6);
          const lo = Math.floor(v);
          const idx = v - lo > d ? Math.min(lo + 1, SKY.length - 1) : lo;
          put(x, y, SKY[idx]);
          skyMask[y * W + x] = glow > 0.15 ? 0 : 1;
          openSky[y * W + x] = 1;
        }
      }
    }
  }

  const stars = [];
  const starCount = Math.round((W * horizon) / 160);
  for (let i = 0; i < starCount; i++) {
    const x = Math.floor(rand() * W);
    const y = Math.floor(rand() ** 1.4 * horizon * 0.85);
    if (!skyMask[y * W + x]) continue;
    stars.push({
      x,
      y,
      b: 0.25 + rand() ** 3 * 0.75,
      phase: rand() * Math.PI * 2,
      speed: 0.6 + rand() * 2.2,
    });
  }

  const reeds = [];
  for (let x = 2; x < W - 2; x++) {
    if (rand() < 0.16) reeds.push({ x, h: 2 + Math.floor(rand() * 7), phase: rand() * 6 });
  }

  const fireflies = Array.from({ length: Math.max(6, Math.round(W / 22)) }, () => ({
    x: rand() * W,
    y: lakeEnd - H * 0.02 - rand() * H * 0.14,
    ax: 4 + rand() * 10,
    ay: 2 + rand() * 5,
    phase: rand() * 6,
    speed: 0.15 + rand() * 0.35,
    ox: 0,
    oy: 0,
  }));

  // Dim toward a soft oval and dissolve into the page, so there is no frame.
  // 0 means the pixel is gone, 255 means untouched.
  const edge = new Uint8Array(W * H);
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const nx = Math.abs((x - W / 2) / (W / 2));
      const ny = Math.abs((y - H * 0.48) / (H * 0.5));
      const d = (nx ** 3 + ny ** 3) ** (1 / 3);
      const f = Math.max(0, Math.min(1, (1 - d) / 0.4));
      edge[y * W + x] = f * f < bayer(x, y) * 0.6 ? 0 : Math.round(f * 255);
    }
  }

  const henH = Math.max(8, Math.round(moon.r * 0.95));
  const henW = Math.round(henH * 0.95);
  const henX = Math.round(moon.x - henW * 0.42);
  const henY = crest - henH + 1;

  return {
    W, H, horizon, lakeEnd, moon, crest, base, skyMask, openSky, stars, reeds, fireflies, edge, groundY,
    henX, henY, henH,
  };
}

// `clock.start` is set by the page when the opening begins (null until then).
// `onHen` hears where the hen sits on screen, so the opening can fly it there.
function PixelScene({ clock, onHen }) {
  const canvasRef = useRef(null);
  const onHenRef = useRef(onHen);
  onHenRef.current = onHen;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let scene = null;
    let image = null;
    let hen = null;
    let raf = 0;
    let last = 0;
    let visible = true;
    const t0 = performance.now();
    let nextShootingStar = 3 + Math.random() * 4;
    let shootingStar = null;
    let lastRender = 0;
    const pointer = { x: 0, y: 0, inside: false, rippleX: -99, rippleY: -99 };
    let ripples = [];
    let scenePx = 3;
    const introElapsed = () => (clock.start === null ? -1 : performance.now() - clock.start);

    const reportHen = () => {
      if (!hen || !scene || hen.h !== scene.henH || !onHenRef.current) return;
      const rect = canvas.getBoundingClientRect();
      onHenRef.current({
        w: hen.w,
        h: hen.h,
        mask: hen.mask,
        rgba: hen.rgba,
        left: rect.left + scene.henX * scenePx,
        top: rect.top + scene.henY * scenePx,
        cell: scenePx,
      });
    };

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      const PX = pixelSize(rect.width);
      const W = Math.max(60, Math.ceil(rect.width / PX));
      const H = Math.max(80, Math.ceil(rect.height / PX));
      if (scene && scene.W === W && scene.H === H) return;
      scene = buildScene(W, H);
      scenePx = PX;
      canvas.width = W;
      canvas.height = H;
      canvas.style.width = `${W * PX}px`;
      canvas.style.height = `${H * PX}px`;
      image = ctx.createImageData(W, H);
      if (!hen || hen.h !== scene.henH) {
        loadHen(scene.henH).then((sprite) => {
          hen = sprite;
          reportHen();
          render((performance.now() - t0) / 1000);
        });
      } else {
        reportHen();
      }
      render((performance.now() - t0) / 1000);
    };

    const render = (t) => {
      const {
        W, H, horizon, lakeEnd, moon, base, openSky, stars, reeds, fireflies, edge, groundY,
      } = scene;
      const px = image.data;
      px.set(base);
      const frames = Math.min(3, Math.max(0, (t - lastRender) * 30));
      lastRender = t;

      const blend = (x, y, c, a) => {
        if (x < 0 || y < 0 || x >= W || y >= H) return;
        const i = (y * W + x) * 4;
        px[i] += (c[0] - px[i]) * a;
        px[i + 1] += (c[1] - px[i + 1]) * a;
        px[i + 2] += (c[2] - px[i + 2]) * a;
      };

      for (const s of stars) {
        const b = s.b * (0.55 + 0.45 * Math.sin(t * s.speed + s.phase));
        blend(s.x, s.y, STAR, b);
        if (s.b > 0.8 && b > 0.6) {
          blend(s.x - 1, s.y, STAR, b * 0.35);
          blend(s.x + 1, s.y, STAR, b * 0.35);
          blend(s.x, s.y - 1, STAR, b * 0.35);
          blend(s.x, s.y + 1, STAR, b * 0.35);
        }
      }

      if (!reduceMotion) {
        if (!shootingStar && t > nextShootingStar) {
          shootingStar = {
            x: W * (0.3 + Math.random() * 0.7),
            y: horizon * (0.05 + Math.random() * 0.25),
            start: t,
          };
        }
        if (shootingStar) {
          const age = t - shootingStar.start;
          const head = age * W * 0.9;
          for (let k = 0; k < 14; k++) {
            const d = head - k;
            if (d < 0) continue;
            const x = Math.round(shootingStar.x - d);
            const y = Math.round(shootingStar.y + d * 0.45);
            if (y < H && x >= 0 && openSky[y * W + x]) {
              blend(x, y, STAR, (1 - k / 14) * (1 - age / 0.9));
            }
          }
          if (age > 0.9) {
            shootingStar = null;
            nextShootingStar = t + 6 + Math.random() * 9;
          }
        }

        // A small flock drifts across the moon now and then
        const loop = 26;
        const cycle = (t % loop) / loop;
        for (let b = 0; b < 3; b++) {
          const bx = Math.round(-10 + cycle * (W + 30) - b * 6);
          const by = Math.round(moon.y - moon.r * 0.55 + b * 3 + Math.sin(t * 1.3 + b) * 1.5);
          const up = Math.floor(t * 5 + b) % 2 === 0;
          const shape = up
            ? [[-2, -1], [-1, 0], [0, 0], [1, 0], [2, -1]]
            : [[-2, 0], [-1, 0], [0, 0], [1, 0], [2, 0]];
          for (const [dx, dy] of shape) blend(bx + dx, by + dy, BIRDS, 1);
        }
      }

      if (hen && introElapsed() >= INTRO.flyEnd) {
        const pecking = !reduceMotion && t % 6 > 5.1 && Math.floor(t * 6) % 2 === 0;
        const hx = scene.henX;
        const hy = scene.henY;
        // The sprite faces left; turn around when the cursor is to the right
        const flip = pointer.inside && pointer.x > hx + hen.w / 2;
        for (let y = 0; y < hen.h; y++) {
          for (let x = 0; x < hen.w; x++) {
            if (!hen.mask[y * hen.w + x]) continue;
            // Peck: dip the head (front, upper part of the sprite)
            const head = pecking && x < hen.w * 0.42 && y < hen.h * 0.5;
            const dy = head ? Math.max(1, Math.round(hen.h * 0.12)) : 0;
            const sx = (flip ? hen.w - 1 - x : x) + (head ? (flip ? 1 : -1) : 0);
            blend(hx + sx, hy + y + dy, HILL, 1);
          }
        }
      }

      // Lake: mirror everything above the horizon with a moving ripple
      const lakeH = lakeEnd - horizon;
      for (let y = horizon; y < H; y++) {
        const depth = (y - horizon) / lakeH;
        const amp = reduceMotion ? 0 : 0.6 + depth * 2.4;
        const shift = Math.round(
          Math.sin(t * 1.8 + y * 0.85) * amp + Math.sin(t * 0.7 + y * 0.31) * amp * 0.5
        );
        const srcY = Math.max(0, horizon - 1 - (y - horizon));
        for (let x = 0; x < W; x++) {
          if (y >= groundY[x] && y >= lakeEnd - H * 0.02) continue;
          const sx = Math.min(W - 1, Math.max(0, x + shift));
          const si = (srcY * W + sx) * 4;
          const i = (y * W + x) * 4;
          const r = px[si];
          const g = px[si + 1];
          const b = px[si + 2];
          // Break the moon's reflection into drifting horizontal glints
          const bright = r > 150;
          const streak = Math.sin(x * 0.35 + y * 2.1 + t * 2.4) + Math.sin(y * 0.9 - t * 1.3);
          const gap = bright && streak < -0.4 + depth;
          const k = bright ? (gap ? 0.12 : 0.62 - depth * 0.2) : 0.5 - depth * 0.15;
          px[i] = r * k + WATER[0] * (1 - k);
          px[i + 1] = g * k + WATER[1] * (1 - k);
          px[i + 2] = b * k + WATER[2] * (1 - k);
          px[i + 3] = 255;
        }
      }

      ripples = ripples.filter((r) => t - r.start < RIPPLE_LIFE);
      for (const r of ripples) {
        const age = t - r.start;
        if (age < 0) continue;
        const radius = 1 + age * 16 * r.strength;
        const alpha = (1 - age / RIPPLE_LIFE) * 0.55 * r.strength;
        const steps = Math.max(16, Math.round(radius * 6));
        for (let k = 0; k < steps; k++) {
          const a = (k / steps) * Math.PI * 2;
          const x = Math.round(r.x + Math.cos(a) * radius);
          const y = Math.round(r.y + Math.sin(a) * radius * 0.32);
          if (y < horizon || y >= H || x < 0 || x >= W) continue;
          if (y >= groundY[x] && y >= lakeEnd - H * 0.02) continue;
          blend(x, y, RIPPLE, alpha);
        }
      }

      for (const r of reeds) {
        const top = groundY[r.x];
        const sway = reduceMotion ? 0 : Math.round(Math.sin(t * 1.4 + r.phase) * 0.8);
        for (let k = 0; k < r.h; k++) {
          const x = r.x + (k > r.h * 0.6 ? sway : 0);
          blend(x, top - k, GROUND, 1);
        }
      }

      for (const f of fireflies) {
        const bx = f.x + Math.sin(t * f.speed + f.phase) * f.ax;
        const by = f.y + Math.cos(t * f.speed * 1.3 + f.phase) * f.ay;
        let tx = 0;
        let ty = 0;
        if (pointer.inside) {
          const orbit = 5 + (f.phase % 1) * 7;
          tx = pointer.x + Math.cos(t * 1.1 + f.phase * 2) * orbit - bx;
          ty = pointer.y + Math.sin(t * 1.4 + f.phase * 2) * orbit * 0.7 - by;
        }
        const ease = 1 - Math.pow(0.96, frames);
        f.ox += (tx - f.ox) * ease;
        f.oy += (ty - f.oy) * ease;
        const x = Math.round(bx + f.ox);
        const y = Math.round(by + f.oy);
        const glow = reduceMotion ? 0.7 : Math.max(0, Math.sin(t * 1.7 + f.phase * 3));
        if (glow <= 0.05) continue;
        blend(x, y, FIREFLY, glow);
        blend(x - 1, y, FIREFLY, glow * 0.25);
        blend(x + 1, y, FIREFLY, glow * 0.25);
        blend(x, y - 1, FIREFLY, glow * 0.25);
        blend(x, y + 1, FIREFLY, glow * 0.25);
      }

      if (pointer.inside) {
        const cx = Math.round(pointer.x);
        const cy = Math.round(pointer.y);
        for (let y = cy - LANTERN_RADIUS; y <= cy + LANTERN_RADIUS; y++) {
          for (let x = cx - LANTERN_RADIUS; x <= cx + LANTERN_RADIUS; x++) {
            const f = 1 - Math.hypot(x - pointer.x, y - pointer.y) / LANTERN_RADIUS;
            if (f > bayer(x, y)) blend(x, y, LANTERN, 0.1 + f * 0.28);
          }
        }
      }

      // Stay dark until the opening starts; from then on its overlay does the reveal
      if (clock.start === null) {
        for (let i = 3; i < px.length; i += 4) px[i] = 0;
      }

      for (let i = 0; i < edge.length; i++) {
        const j = i * 4;
        const keep = px[j + 3] === 0 ? 0 : edge[i] / 255;
        if (keep < 1) {
          // Darken toward the page colour before the dither cuts in
          const k = keep === 0 ? 0 : 0.35 + keep * 0.65;
          px[j] = BG[0] + (px[j] - BG[0]) * k;
          px[j + 1] = BG[1] + (px[j + 1] - BG[1]) * k;
          px[j + 2] = BG[2] + (px[j + 2] - BG[2]) * k;
          px[j + 3] = 255;
        }
      }

      ctx.putImageData(image, 0, 0);
    };

    const tick = (now) => {
      raf = visible ? requestAnimationFrame(tick) : 0;
      const busy = pointer.inside || introElapsed() < INTRO.revealEnd;
      if (now - last < 1000 / (busy ? 30 : FPS)) return;
      last = now;
      render((now - t0) / 1000);
    };

    const now = () => (performance.now() - t0) / 1000;

    const toScene = (e) => {
      const rect = canvas.getBoundingClientRect();
      return {
        x: ((e.clientX - rect.left) / rect.width) * scene.W,
        y: ((e.clientY - rect.top) / rect.height) * scene.H,
      };
    };

    const onPointerMove = (e) => {
      const { x, y } = toScene(e);
      pointer.x = x;
      pointer.y = y;
      pointer.inside = true;
      // Trail small ripples while moving across the lake
      const onLake = y >= scene.horizon && y < scene.lakeEnd;
      if (!reduceMotion && onLake && Math.hypot(x - pointer.rippleX, y - pointer.rippleY) > 6) {
        ripples.push({ x, y, start: now(), strength: 0.6 });
        if (ripples.length > 14) ripples.shift();
        pointer.rippleX = x;
        pointer.rippleY = y;
      }
      if (reduceMotion) render(now());
    };

    const onPointerLeave = () => {
      pointer.inside = false;
      if (reduceMotion) render(now());
    };

    // Click the sky for a shooting star, click the lake for a splash
    const onPointerDown = (e) => {
      if (reduceMotion) return;
      const { x, y } = toScene(e);
      if (y < scene.horizon) {
        shootingStar = { x, y, start: now() };
      } else if (y < scene.lakeEnd) {
        ripples.push({ x, y, start: now(), strength: 1 });
        ripples.push({ x, y, start: now() + 0.18, strength: 0.8 });
      }
    };

    const onPointerUp = (e) => {
      if (e.pointerType !== "mouse") onPointerLeave();
    };

    canvas.style.cursor = pixelCursor();
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerleave", onPointerLeave);
    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointerup", onPointerUp);
    const removePointer = () => {
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerleave", onPointerLeave);
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointerup", onPointerUp);
    };

    const ro = new ResizeObserver(resize);
    ro.observe(canvas.parentElement);
    resize();
    window.addEventListener("scroll", reportHen, { passive: true });

    if (reduceMotion) {
      return () => {
        ro.disconnect();
        removePointer();
        window.removeEventListener("scroll", reportHen);
      };
    }

    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible && !raf) raf = requestAnimationFrame(tick);
    });
    io.observe(canvas);
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      removePointer();
      window.removeEventListener("scroll", reportHen);
    };
  }, [clock]);

  return (
    <canvas
      ref={canvasRef}
      className="scene-canvas"
      role="img"
      aria-label="Pixel art of a moonlit lake at night, with a hen standing on a hill against the moon"
    />
  );
}

export default PixelScene;
